// Top-level route classifier used by main.tsx to decide whether a URL is one
// we recognize, or whether we should hand it off to NotFoundPage.
//
// Kept as a pure function so the classification rules are testable in
// isolation — the actual routing wiring in main.tsx just calls this once and
// switches on the result.
//
// Rules of thumb:
//   1. app-internal wrapper paths (/helper*, /member*) are ONLY recognized
//      when we're actually inside the native wrapper. On the web they are
//      leaks and must resolve to a plain "not found" — never a page that
//      names the wrapper, hints at an APK, or otherwise exposes internals.
//      Local mock/debug previews may opt in explicitly so visual regression
//      checks can exercise the same route tree in a browser.
//   2. deep links (/join/{token}, /invite/{token}, /invite-review/{token},
//      /field-pass/{token}, /verify/{code}) and shared report links (/shared/reports/{id})
//      are always recognized so the public link flows can run.
//   3. console-scoped paths use the shared CONSOLE_ROOTS list from
//      utils/consoleRoutes to stay in sync with the router that consumes
//      them; new console surfaces are picked up automatically.
//   4. anything else is unknown → NotFoundPage.
import { isConsolePath, isDesktopConsoleRoute } from './consoleRoutes'
import { isQuoteToken } from '../types/ops'
import { isSharedDevicePath } from './sharedDeviceRoute'

const OWNER_CONSOLE_PATH = '/sole-con-mana-ment-ge'

const DEEP_LINK_PREFIXES = ['/join/', '/invite/', '/invite-review/', '/field-pass/', '/verify/'] as const

const APP_ONLY_PREFIXES = ['/helper', '/member'] as const

export type TopLevelRouteKind =
  | 'public'
  | 'deep-link'
  | 'native-wrapper'
  | 'console'
  | 'family'
  | 'owner-console'
  | 'shared-report'
  | 'shared-report-card'
  | 'client-opt-out'
  | 'client-form'
  | 'client-quote'
  | 'place-scan'
  | 'public-booking'
  | 'edu-enquiry'
  | 'edu-ptm-join'
  | 'verify-evidence'
  | 'shared-device'
  | 'guest-request-status'
  | 'unknown'

const startsWithAny = (pathname: string, prefixes: readonly string[]): boolean =>
  prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))

const isDeepLinkPath = (pathname: string): boolean =>
  pathname === '/join' ||
  pathname === '/account/transfer/confirm' ||
  DEEP_LINK_PREFIXES.some((prefix) => pathname.startsWith(prefix) && pathname.length > prefix.length)

const isSharedReportPath = (pathname: string): boolean =>
  /^\/shared\/reports\/[^/]+$/.test(pathname)

const isSharedReportCardPath = (pathname: string): boolean =>
  /^\/shared\/report-cards\/[^/]+$/.test(pathname)

const isFamilyPath = (pathname: string): boolean =>
  pathname === '/family' || pathname.startsWith('/family/')

const isClientOptOutPath = (pathname: string): boolean =>
  /^\/o\/[^/]+$/.test(pathname)

const isClientFormPath = (pathname: string): boolean =>
  /^\/f\/[^/]+$/.test(pathname)

const isClientQuotePath = (pathname: string): boolean => {
  const match = pathname.match(/^\/q\/([^/]+)$/)
  return Boolean(match && isQuoteToken(match[1]))
}

const isPlaceScanPath = (pathname: string): boolean => {
  const pqMatch = pathname.match(/^\/pq\/([^/]+)$/)
  if (pqMatch) return true
  const match = pathname.match(/^\/q\/([^/]+)$/)
  return Boolean(match && !isQuoteToken(match[1]))
}

const isPublicBookingPath = (pathname: string): boolean =>
  /^\/book\/[^/]+$/.test(pathname)

const isEduEnquiryPath = (pathname: string): boolean =>
  /^\/enquire\/[^/]+$/.test(pathname)

const isEduPtmJoinPath = (pathname: string): boolean =>
  pathname === '/edu/ptm/join'

export const isGuestRequestStatusPath = (pathname: string): boolean =>
  /^\/gr\/[^/]+$/.test(pathname)

const isAppOnlyPath = (pathname: string): boolean => startsWithAny(pathname, APP_ONLY_PREFIXES)

export type ClassifierInput = {
  pathname: string
  publicSitePaths: ReadonlySet<string>
  isNativeWrapper: boolean
  allowBrowserPreviewRoutes?: boolean
}

export const classifyTopLevelRoute = ({
  pathname,
  publicSitePaths,
  isNativeWrapper,
  allowBrowserPreviewRoutes = false,
}: ClassifierInput): TopLevelRouteKind => {
  if (!pathname || typeof pathname !== 'string') return 'unknown'
  if (publicSitePaths.has(pathname)) return 'public'
  if (pathname === '/verify-evidence') return 'verify-evidence'
  if (pathname === OWNER_CONSOLE_PATH || pathname.startsWith(`${OWNER_CONSOLE_PATH}/`)) return 'owner-console'
  if (isDeepLinkPath(pathname)) return 'deep-link'
  if (isSharedReportPath(pathname)) return 'shared-report'
  if (isSharedReportCardPath(pathname)) return 'shared-report-card'
  if (isFamilyPath(pathname)) return 'family'
  if (isClientOptOutPath(pathname)) return 'client-opt-out'
  if (isClientFormPath(pathname)) return 'client-form'
  if (isClientQuotePath(pathname)) return 'client-quote'
  if (isPlaceScanPath(pathname)) return 'place-scan'
  if (isGuestRequestStatusPath(pathname)) return 'guest-request-status'
  if (isPublicBookingPath(pathname)) return 'public-booking'
  if (isEduEnquiryPath(pathname)) return 'edu-enquiry'
  if (isEduPtmJoinPath(pathname)) return 'edu-ptm-join'
  if (isSharedDevicePath(pathname)) return 'shared-device'
  if (isAppOnlyPath(pathname)) return isNativeWrapper || allowBrowserPreviewRoutes ? 'native-wrapper' : 'unknown'
  if ((import.meta.env.DEV || allowBrowserPreviewRoutes) && (pathname === '/dev' || pathname.startsWith('/dev/'))) return 'console'
  if (isConsolePath(pathname) || isDesktopConsoleRoute(pathname)) return 'console'
  return 'unknown'
}

/**
 * Should we stash this path for a post-login redirect? Only recognized paths
 * that make sense once the user is signed in. Public and unknown are excluded
 * (they render fine or 404 respectively without login), deep links and
 * native-wrapper paths have their own claim/handling flow, and the owner
 * console is security-sensitive so callers always re-type it.
 */
export const shouldStashForPostLogin = (kind: TopLevelRouteKind): boolean =>
  kind === 'console' ||
  kind === 'family' ||
  kind === 'shared-report-card' ||
  kind === 'edu-ptm-join' ||
  kind === 'place-scan'

export type VerifyRouteState =
  | { kind: 'NONE' }
  | { kind: 'VERIFY'; code: string }

export const detectVerifyRoute = (pathname?: string): VerifyRouteState => {
  const path = pathname !== undefined
    ? pathname
    : (typeof window !== 'undefined' ? window.location.pathname : '')
  if (!path.startsWith('/verify/')) return { kind: 'NONE' }
  const code = path.slice('/verify/'.length).split('/')[0]
  return code ? { kind: 'VERIFY', code } : { kind: 'NONE' }
}

export const detectVerifyEvidenceRoute = (pathname?: string): boolean => {
  const path = pathname !== undefined
    ? pathname
    : (typeof window !== 'undefined' ? window.location.pathname : '')
  return path === '/verify-evidence'
}

export type AccountTransferConfirmRouteState =
  | { kind: 'NONE' }
  | { kind: 'CONFIRM'; code: string; token: string }

export const detectAccountTransferConfirmRoute = (
  pathname?: string,
  search?: string,
): AccountTransferConfirmRouteState => {
  const path = pathname !== undefined
    ? pathname
    : (typeof window !== 'undefined' ? window.location.pathname : '')
  if (path !== '/account/transfer/confirm') return { kind: 'NONE' }
  const s = search !== undefined
    ? search
    : (typeof window !== 'undefined' ? window.location.search : '')
  const params = new URLSearchParams(s)
  const code = params.get('code') ?? ''
  const token = params.get('t') ?? ''
  return { kind: 'CONFIRM', code, token }
}

